import { type LinkProps } from 'next/dist/client/link';
import React from 'react';
import { LinkWithQuery } from '@/components/elements/LinkWithQuery';
import { useMounted } from '@/hooks/useMounted';
import LoadingIcon from '@/public/icons/loader.svg';
import styles from './Button.module.scss';

export type buttonPresets =
	| 'primary'
	| 'landing_outline'
	| 'outline'
	| 'landing'
	| 'landing-pink'
	| 'secondary'
	| 'black'
	| 'white'
	| 'link';

export interface BaseProps {
	text?: string | React.ReactNode;
	preset?: buttonPresets;
	Icon?: React.ElementType<React.SVGProps<unknown>>;
	iconPosition?: 'left' | 'right';
	size?: 'small' | 'medium' | 'large';
	landing?: boolean;
	stylePreset?: 'landing_outline' | 'landing_icon';
	disabled?: boolean;
	loading?: boolean;
	ctrAnimation?: boolean;
	fullWidth?: boolean;
	rounded?: boolean;
	classes?: classesTypes;
	autoOnFocus?: autoFocusTypes;
}

interface classesTypes {
	button?: string;
	icon?: string;
	text?: string;
	loading?: string;
	loadingIcon?: string;
	disabled?: string;
}
interface autoFocusTypes {
	focus: boolean;
	delay?: number;
}

export type ButtonProps = BaseProps &
	(
		| (React.ButtonHTMLAttributes<HTMLButtonElement> & {
				as: 'button';
		  })
		| (React.AnchorHTMLAttributes<HTMLAnchorElement> &
				LinkProps & {
					as: 'link';
				})
		| (React.HTMLAttributes<HTMLDivElement> & {
				as: 'div';
		  })
	);

export const Button: React.FC<ButtonProps> = ({
	as,
	text,
	Icon,
	iconPosition,
	preset = 'primary',
	size = 'medium',
	disabled,
	loading,
	ctrAnimation,
	fullWidth,
	rounded,
	classes,
	autoOnFocus,
	landing,
	stylePreset,
	...props
}) => {
	// Helpers
	const isLink = as === 'link';
	const isDiv = as === 'div';
	const getTag = () => {
		if (isLink) return LinkWithQuery;
		if (isDiv) return 'div';
		return 'button';
	};
	const Tag = getTag() as React.ElementType;
	// - hooks
	const mounted = useMounted();
	// - methods
	const getButtonRef = (element: HTMLButtonElement | HTMLAnchorElement) => {
		// Set focus
		if (autoOnFocus?.focus && element && !mounted) {
			setTimeout(() => {
				element?.focus();
			}, autoOnFocus?.delay);
		}
	};

	return (
		<Tag
			ref={getButtonRef}
			className={`
				${styles['button']}
				${!text ? styles['button_no-text'] : ''}
				${fullWidth ? styles[`button_fullWidth`] : ''}
				${iconPosition ? styles[`button_withAbsoluteIcon`] : ''}
				${rounded ? styles[`button_rounded`] : ''}
				${size ? styles[`button_${size}`] : ''}
				${loading ? styles['button_loading'] : ''}
				${ctrAnimation ? styles['button_animation'] : ''}
				${preset ? styles[`button_${preset}`] : ''}
				${(isDiv || isLink) && (disabled || loading) ? styles['button_disabled'] : ''}
				${classes?.button ?? ''}
				${disabled || loading ? (classes?.disabled ?? '') : ''}
			`}
			{...props}
			disabled={(!isDiv || !isLink) && (disabled || loading)}
		>
			<span className={styles['button__wrapper']}>
				{Icon && (
					<Icon
						className={`
						${styles['button__icon']}
						${iconPosition ? styles[`button__icon_${iconPosition}`] : ''}
						${classes?.icon ?? ''}
					`}
					/>
				)}

				{text ? (
					<span
						className={`
					${styles['button__text']}
					${classes?.text ?? ''}
				`}
					>
						{text}
					</span>
				) : null}

				<span
					className={`
					${styles['button__loading']}
					${classes?.loading ?? ''}
				`}
				>
					<LoadingIcon
						className={`
						${styles['button__loading-icon']}
						${classes?.loadingIcon ?? ''}
					`}
					/>
				</span>
			</span>
		</Tag>
	);
};
